import { useState, useMemo } from 'react'
import Button from './Button'
import { SearchQuery } from '../types'
import './SearchForm.css'

export interface SearchFormProps {
  onSearch: (query: SearchQuery) => void
  loading?: boolean
}

function SearchForm({ onSearch, loading = false }: SearchFormProps) {
  const [searchCode, setSearchCode] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [showHelp, setShowHelp] = useState(false)

  const isValid = useMemo(() => {
    return searchCode.trim() !== '' || companyName.trim() !== ''
  }, [searchCode, companyName])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    const query: SearchQuery = {}
    if (searchCode.trim()) query.productName = searchCode.trim().toUpperCase()
    if (companyName.trim()) query.lotNumber = companyName.trim()
    onSearch(query)
  }

  const handleReset = () => {
    setSearchCode('')
    setCompanyName('')
  }

  return (
    <div className="search-form-container">
      <form className="search-form" onSubmit={handleSubmit} noValidate>
        <div className="search-form__row">
          <div className="search-form__field">
            <label htmlFor="searchCode" className="search-form__label">Search Code / Accession #</label>
            <input
              id="searchCode"
              type="text"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              maxLength={50}
              placeholder="e.g. ABCD2603170030"
              className="search-form__input"
            />
          </div>
          <div className="search-form__divider">
            <span>or</span>
          </div>
          <div className="search-form__field">
            <label htmlFor="companyName" className="search-form__label">Company Name</label>
            <input
              id="companyName"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              maxLength={200}
              placeholder="Enter company name"
              className="search-form__input"
            />
          </div>
          <div className="search-form__actions">
            <Button type="submit" disabled={!isValid || loading} loading={loading}>
              Look up
            </Button>
            {(searchCode || companyName) && (
              <button type="button" className="search-form__clear" onClick={handleReset} tabIndex={-1}>
                &#x2715;
              </button>
            )}
          </div>
        </div>
      </form>

      <button
        type="button"
        className={`search-form__help-toggle ${showHelp ? 'open' : ''}`}
        onClick={() => setShowHelp(!showHelp)}
      >
        {showHelp ? 'Hide' : 'How to find your Search Code'}
        <span className="search-form__help-arrow">{showHelp ? '▾' : '▸'}</span>
      </button>

      {showHelp && (
        <div className="search-form__help-panel">
          <div className="search-form__help-col">
            <h4>How to Find Your Search Code</h4>
            <ol>
              <li>Locate your Certificate of Analysis document.</li>
              <li>Find the <strong>Search Code</strong> field in the header section of the CoA.</li>
              <li>Enter that code and click Look up. The Search Code is the first 4 characters of your company name followed by your accession number.</li>
            </ol>
            <p className="search-form__example">
              <strong>Example:</strong> ABCD2603170030 <span className="search-form__example-note">(4 chars of name + accession #)</span>
            </p>
          </div>
          <div className="search-form__help-col">
            <h4>Search by Company Name</h4>
            <ol>
              <li>Enter your full company name exactly as it appears on your CoA.</li>
              <li>Click <strong>Look up</strong> to see all CoAs associated with your company.</li>
              <li>Click <strong>View</strong> next to any result to view that Certificate of Analysis.</li>
            </ol>
            <p className="search-form__note">Codes and names are not case-sensitive.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchForm
